import RouteFallback from '@/components/ErrorBoundary/RouteFallback';
import { ThemeProvider, styled } from '@mui/material';
import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { HEADER_HEIGHT, HIDDEN_SIDEBAR_WIDTH, SIDEBAR_WIDTH } from '../../constants/layout';
import { MAIN_THEME_TYPE, getCurrentTheme } from '../../theme';
import Ribbon from '../Ribbon';
import Sidebar from '../Sidebar';
import SidebarToggleButton from '../Sidebar/SidebarToggleButton';
import TopBar from '../TopBar/TopBar';
import LoadingSkeleton from './LoadingSkeleton';

const StyledMain = styled('main', {
  shouldForwardProp: prop => prop !== 'sidebarOpen',
})(({ theme, sidebarOpen }) => ({
  flexGrow: 1,
  minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
  maxWidth: `calc(100vw - ${HIDDEN_SIDEBAR_WIDTH}px)`,
  transition: theme.transitions.create('max-width', {
    easing: theme.transitions.easing.sharp,
    duration: sidebarOpen ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
  }),
  '@media print': {
    maxWidth: '100vw',
  },
  variants: [
    {
      props: ({ sidebarOpen }) => sidebarOpen,
      style: {
        maxWidth: `calc(100vw - ${SIDEBAR_WIDTH}px)`,
      },
    },
  ],
}));

const handleMapResize = () => {
  const mountedMap = document.querySelector('.mapboxgl-canvas');

  if (mountedMap) {
    setTimeout(() => {
      const mapContainer = mountedMap.closest('.mapboxgl-map');

      mountedMap.width = mapContainer.getBoundingClientRect().width;
      window.dispatchEvent(new Event('resize'));
    }, 200);
  }
};

const AppLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
    handleMapResize();
  };

  const { whitelabelRequestId: loading, whitelabel } = useSelector(state => state.currentWhitelabel);

  if (loading) {
    return <LoadingSkeleton />;
  }

  return (
    <>
      <Ribbon sidebarOpen={sidebarOpen} />
      <TopBar sidebarOpen={sidebarOpen} handleSidebarToggle={handleSidebarToggle} />
      {/*
        No overflow-y-auto here (flank#4652 review): this div has no fixed
        height, so its content never actually overflows it — the window
        scrolls instead. `overflow: auto` still marks it a scroll container
        per spec regardless, which gave `position: sticky` descendants (the
        Reporting V2 header) a scroll frame of reference that never fires a
        real scroll, so they never stuck. Leaving overflow unset here lets
        sticky resolve against the window, the thing that's actually scrolling.
      */}
      <div className='flex bg-[#f9f8f8]'>
        <ThemeProvider theme={getCurrentTheme(whitelabel, MAIN_THEME_TYPE)}>
          <Sidebar
            sidebarOpen={sidebarOpen}
            handleSidebarToggle={handleSidebarToggle}
            logoSrc={whitelabel.sidebarLogo}
          />
        </ThemeProvider>
        <SidebarToggleButton sidebarOpen={sidebarOpen} handleSidebarToggle={handleSidebarToggle} />
        <StyledMain sidebarOpen={sidebarOpen}>
          <ErrorBoundary FallbackComponent={RouteFallback} onReset={() => location.reload()}>
            <Outlet />
          </ErrorBoundary>
        </StyledMain>
      </div>
    </>
  );
};

export default AppLayout;
